import React, { useEffect, useState, useRef, useCallback } from 'react';
import { usePusher } from '../../App';
import { fDate, fTimeDateUSFormat } from '@/actions/Helper';
import styles from './styles';
import { useNotify } from 'react-admin';
import {
  disableInventoryBooking,
  enableInventoryBooking,
  getInventoryShareLink,
  SaveApiResMessage,
} from '@/actions';
import Bookings from './Bookings';
import { Icons } from '@/assets';
import { ReactSVG } from 'react-svg';
import LoadingV2 from '@/components/Loading';
import Modal from '@/ctComponents/Modal';
import IconButtonV2 from '@/ctComponents/IconButton/IconButtonV2';
import CustomCheckbox from '@/components/CustomCheckbox';
import Tooltip from '@/components/Tooltip';

export default ({
  DATA = [],
  Del,
  Edit,
  getExportInventoryList,
  getExportInventoryListNotLoading,
  currentPage,
  tableTheadTitle,
}) => {
  const cls = styles();
  const [bookingsData, setBookingsData] = useState(DATA);
  const { channels } = usePusher();
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [refreshInventories, setRefreshInventories] = useState(false);
  const [shareLink, setShareLink] = useState({
    loading: null,
    DATA: null,
  });

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState({
    show: false,
    itemId: null,
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: '' });
  const [sortedData, setSortedData] = useState([]);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  const mainScroll = document.getElementsByTagName('main')[0];
  // Sortable columns configuration - matches the actual column names from tableTheadTitle
  const sortableColumns = {
    size: 'size',
    ssl: 'ssl',
    loc: 'loc',
    gated_out: 'gated_out',
    gated_in: 'gated_in',
    vessel_name: 'vessel_name',
    vessel_voyage_in: 'vessel_voyage_in',
    appointment_date: 'appointment_date',
    load_pickup_date: 'load_pickup_date',
    erd: 'erd',
    cutoff: 'cutoff',
    pick_up_location: 'pick_up_location',
    return_to_terminal: 'return_to_terminal',
    vessel_departure: 'vessel_departure',
    date: 'date',
    up: 'up',
    name: 'name',
    container: 'container',
  };

  // Sort function
  const sortBy = (key) => {
    // Define which columns should default to ascending (alphabetical) vs descending (dates/numbers)
    const alphabeticalColumns = [
      'size',
      'ssl',
      'loc',
      'vessel_name',
      'vessel_voyage_in',
      'pick_up_location',
      'return_to_terminal',
      'name',
      'container',
    ];
    const dateColumns = [
      'gated_out',
      'gated_in',
      'appointment_date',
      'load_pickup_date',
      'erd',
      'cutoff',
      'vessel_departure',
      'date',
      'up',
    ];

    const isAlphabetical = alphabeticalColumns.includes(key);
    const isDate = dateColumns.includes(key);

    // Default to descending for dates, ascending for alphabetical
    let direction = isDate ? 'descending' : 'ascending';
    if (sortConfig.key === key) {
      // If clicking the same column, toggle direction
      direction =
        sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
    }

    setSortConfig({ key, direction });
  };

  // Sort data when sortConfig changes
  useEffect(() => {
    if (!bookingsData) return;

    if (sortConfig.key && sortConfig.direction) {
      const sorted = [...bookingsData].sort((a, b) => {
        let aValue, bValue;

        // Define date columns for sorting
        const dateColumns = [
          'gated_out',
          'gated_in',
          'appointment_date',
          'load_pickup_date',
          'erd',
          'cutoff',
          'vessel_departure',
          'date',
          'up',
        ];

        // Handle different data structures - check if we need to access booking data
        if (['size', 'ssl', 'loc', 'date', 'up'].includes(sortConfig.key)) {
          // These are parent-level fields
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        } else {
          // These are booking-level fields - we'll sort by the first booking
          const firstBookingA = a.bookings?.[0];
          const firstBookingB = b.bookings?.[0];

          // Handle nested object fields
          if (sortConfig.key === 'return_to_terminal') {
            aValue = firstBookingA?.return_to_terminal?.slug;
            bValue = firstBookingB?.return_to_terminal?.slug;
          } else if (sortConfig.key === 'pick_up_location') {
            aValue = firstBookingA?.pick_up_location?.slug;
            bValue = firstBookingB?.pick_up_location?.slug;
          } else {
            // Direct booking fields
            aValue = firstBookingA?.[sortConfig.key];
            bValue = firstBookingB?.[sortConfig.key];
          }
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        // Special handling for different data types
        if (dateColumns.includes(sortConfig.key)) {
          // Date sorting
          const aDate = aValue ? new Date(aValue) : new Date(0);
          const bDate = bValue ? new Date(bValue) : new Date(0);

          // Handle invalid dates
          const aTime = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
          const bTime = isNaN(bDate.getTime()) ? 0 : bDate.getTime();

          if (sortConfig.direction === 'ascending') {
            return aTime - bTime;
          } else {
            return bTime - aTime;
          }
        } else {
          // String sorting for other columns
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();

          if (aValue < bValue)
            return sortConfig.direction === 'ascending' ? -1 : 1;
          if (aValue > bValue)
            return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        }
      });

      setSortedData(sorted);
    } else {
      setSortedData(bookingsData);
    }
  }, [bookingsData, sortConfig]);

  // Get display data
  const getDisplayData = () => {
    return sortConfig.key ? sortedData : bookingsData;
  };

  function replacebookingById(bookingsData, updatedbooking) {
    // Create a new array to ensure immutability
    return bookingsData.map((bookingGroup) => {
      const index = bookingGroup.bookings.findIndex(
        (booking) => booking.id === updatedbooking.id
      );

      if (index !== -1) {
        // Create a new bookings array with the updated booking
        const updatedBookings = bookingGroup.bookings.map((booking, i) =>
          i === index ? updatedbooking : booking
        );

        // Return a new bookingGroup with the updated bookings
        return {
          ...bookingGroup,
          bookings: updatedBookings,
        };
      }

      // If no changes, return the original bookingGroup
      return bookingGroup;
    });
  }

  useEffect(() => {
    if (channels?.privateChannel) {
      channels.privateChannel.bind('Export', (data) => {
        console.log('Export event:', data);
        const updatedbooking = data.message.booking;
        const updatedbookingsData = replacebookingById(
          bookingsData,
          updatedbooking
        );
        setBookingsData(updatedbookingsData);
      });

      return () => {
        channels.privateChannel.unbind('Export');
      };
    }
  }, [channels, bookingsData]);

  useEffect(() => {
    setBookingsData(DATA);
  }, [DATA]);
  const notify = useNotify();

  // Handle booking name click - copy to clipboard and open link after 1 second
  const handleBookingNameClick = useCallback((e, booking, objectKey) => {
    e.preventDefault();
    const bookingNumber = booking[objectKey] || '-';
    const trackingUrl = booking['shipping_line_tracking_url'];

    // Copy booking number to clipboard
    navigator.clipboard
      .writeText(bookingNumber)
      .then((res) => {
        // Show notification
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: `Booking Number "${bookingNumber}" Copied`,
          mode: 'success',
        });

        // Open link in new tab after 1 second
        setTimeout(() => {
          window.open(trackingUrl, '_blank');
        }, 1000);
      })
      .catch((err) => {
        // Fallback if clipboard API fails
        SaveApiResMessage({
          title: 'Error Copying Booking',
          message: `Booking Number "${bookingNumber}" Not Copied`,
          mode: 'success',
        });
        setTimeout(() => {
          window.open(trackingUrl, '_blank');
        }, 1000);
      });
  }, []);

  const getBookingNumbers = (exportList, target) => {
    if (exportList) {
      let activeBookings = [];
      let deactiveBookings = [];
      exportList.bookings.forEach((booking) => {
        if (booking.status === 1) {
          activeBookings.push(booking);
        } else if (booking.status === 0) {
          deactiveBookings.push(booking);
        }
      });

      if (deactiveBookings.length === 0) {
        return (
          <input
            type="checkbox"
            className={`all ${cls.containerChecBox}`}
            checked={true}
          />
        );
      } else if (activeBookings.length === 0) {
        return (
          <input
            type="checkbox"
            className={`any ${cls.containerChecBox}`}
            checked={false}
          />
        );
      } else {
        return (
          <input
            type="checkbox"
            checked={true}
            className={`some ${cls.containerChecBox}`}
          />
        );
      }
    }
  };
  const formatUpdatedAtDate = (dateString) => {
    function isInvalidDate(dateStr) {
      return isNaN(Date.parse(dateStr));
    }

    if (dateString == 'please contact your line') {
      return dateString;
    } else if (dateString == 'TBA') {
      return dateString;
    } else if (isInvalidDate(dateString) == true) {
      return '';
    } else {
      const date = new Date(dateString);
      const formattedDate =
        new Date(date).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        }) +
        ' ' +
        new Date(date).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      // console.log(dateString);
      return formattedDate;
    }
  };

  const handleEnableCongtainer = (containerId, index) => {
    enableInventoryBooking(containerId).then((response) => {
      if (response.success) {
        SaveApiResMessage({
          title: response?.data?.messageHeader,
          message: 'Booking updated',
          mode: 'success',
        });
        // updateContainer(index, 'status', 1);
        // setRefreshInventories(true);
        getExportInventoryListNotLoading();
      } else {
        SaveApiResMessage({
          title: 'Error',
          message: 'somthing went wrong!',
          mode: 'error',
        });
      }
    });
  };

  const handleDisableContainer = (containerId, index) => {
    disableInventoryBooking(containerId).then((response) => {
      if (response.success) {
        SaveApiResMessage({
          title: response?.data?.messageHeader,
          message: 'Booking updated',
          mode: 'success',
        });
        // updateContainers(index, 'status', 0);
        // setRefreshInventories(true);
        getExportInventoryListNotLoading();
      } else {
        SaveApiResMessage({
          title: 'Error',
          message: 'somthing went wrong!',
          mode: 'error',
        });
      }
    });
  };

  const handleChangeContainerStatus = (value, containerId, index) => {
    if (value === true) {
      handleEnableCongtainer(containerId, index);
    } else {
      handleDisableContainer(containerId, index);
    }
  };

  const handleGetShareLink = (i) => {
    setShareLink({ loading: i.id });
    getInventoryShareLink(i.id)
      .then((res) => {
        setShareModal(i);
        setShareLink({
          loading: null,
          DATA: res,
        });
      })
      .catch((err) => {
        setShareLink({ loading: null });
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (showDeleteModal.itemId) {
      Del(showDeleteModal.itemId);
      setShowDeleteModal({ show: false, itemId: null });
    }
  }, [showDeleteModal.itemId, Del]);

  // Handle delete button click - show confirmation modal
  const handleDeleteClick = useCallback((itemId) => {
    setShowDeleteModal({ show: true, itemId });
  }, []);

  useEffect(() => {
    let someCompletedCheckboxs = document.getElementsByClassName('some');
    let allCompletedCheckboxs = document.getElementsByClassName('all');
    let notCompletedCheckboxs = document.getElementsByClassName('any');
    for (let i = 0; i < someCompletedCheckboxs.length; i++) {
      someCompletedCheckboxs[i].indeterminate = true;
    }
    for (let i = 0; i < allCompletedCheckboxs.length; i++) {
      allCompletedCheckboxs[i].indeterminate = false;
    }
    for (let i = 0; i < notCompletedCheckboxs.length; i++) {
      notCompletedCheckboxs[i].indeterminate = false;
      notCompletedCheckboxs[i].checked = false;
    }
  }, [getBookingNumbers, getExportInventoryList]);
  let uniqueIndex = 0;

  const amirbiosRef = useRef(null);
  const tableRef = useRef(null);
  const amirbios1Ref = useRef(null);
  const amirbios2Ref = useRef(null);

  const [tableWidth, setTableWidth] = useState(0);
  const [spanWidth, setSpanWidth] = useState(0);

  const isValidDateTime = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const formatDateTimeToUTC = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const formattedDate =
      `${String(date.getUTCMonth() + 1).padStart(2, '0')}-` + // Months are zero-based
      `${String(date.getUTCDate()).padStart(2, '0')}-` +
      `${String(date.getUTCFullYear()).slice(-2)}`; // Slice the last two digits for the year

    // Check if both hours and minutes are 00
    if (hours === 0 && minutes === 0) {
      return formattedDate; // Return only the date
    } else {
      return ` ${formattedDate} ${String(hours).padStart(2, '0')}:${String(
        minutes
      ).padStart(2, '0')}`; // Return time and date
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (amirbiosRef.current && amirbios1Ref.current) {
        const amirbiosTop = amirbiosRef.current.getBoundingClientRect().top;

        if (amirbiosTop <= 42) {
          amirbios2Ref.current.classList.add(cls.fix);
          tableRef.current.classList.add(cls.tableTopMargin);
        } else {
          amirbios2Ref.current.classList.remove(cls.fix);
          tableRef.current.classList.remove(cls.tableTopMargin);
        }
      }
      syncScroll('amirbios');
    };
    const updateWidth = () => {
      if (tableRef.current && amirbios1Ref.current) {
        const tableCurrentWidth =
          tableRef.current.getBoundingClientRect().width;
        setTableWidth(tableCurrentWidth);
      }
    };

    const updateHeadWidth = () => {
      if (amirbiosRef.current && amirbios2Ref.current) {
        const tableeCurrentWidth =
          amirbiosRef.current.getBoundingClientRect().width;
        setSpanWidth(tableeCurrentWidth);
      }
    };

    const syncScroll = (source) => {
      if (amirbiosRef.current || amirbios2Ref.current) {
        if (source === 'amirbios') {
          // console.log('sss');

          amirbios2Ref.current.scrollLeft = amirbiosRef.current.scrollLeft;
        } else if (source === 'amirbios2') {
          // console.log('www');

          amirbiosRef.current.scrollLeft = amirbios2Ref.current.scrollLeft;
        }
      }
    };

    setTimeout(() => {
      updateWidth();
      updateHeadWidth();
    }, 1500);

    window.updateWidth = updateWidth;
    window.updateHeadWidth = updateHeadWidth;
    window.syncScroll = syncScroll;

    const handleScroll1 = () => syncScroll('amirbios');
    const handleScroll2 = () => syncScroll('amirbios2');

    if (amirbiosRef.current) {
      amirbiosRef.current.addEventListener('scroll', handleScroll1);
    }

    if (amirbios1Ref.current) {
      amirbios2Ref.current.addEventListener('scroll', handleScroll2);
    }

    mainScroll.addEventListener('scroll', handleScroll);
    mainScroll.addEventListener('resize', updateWidth);
    mainScroll.addEventListener('resize', updateHeadWidth);
    mainScroll.addEventListener('scroll', updateWidth);
    mainScroll.addEventListener('scroll', updateHeadWidth);

    // Cleanup on component unmount
    return () => {
      mainScroll.removeEventListener('scroll', handleScroll);
      mainScroll.removeEventListener('resize', updateWidth);
      mainScroll.removeEventListener('resize', updateHeadWidth);
      mainScroll.removeEventListener('scroll', updateWidth);
      mainScroll.removeEventListener('scroll', updateHeadWidth);

      if (amirbiosRef.current) {
        amirbiosRef.current.removeEventListener('scroll', handleScroll1);
      }

      if (amirbios2Ref.current) {
        amirbios2Ref.current.removeEventListener('scroll', handleScroll2);
      }

      // if (amirbios2Ref.current) {
      //   amirbios2Ref.current.removeEventListener('scroll', () =>
      //     syncScroll('amirbios2')
      //   );
      // }
    };
  }, [bookingsData, amirbiosRef, amirbios2Ref]);

  useEffect(() => {
    if (amirbios2Ref.current) {
      amirbios2Ref.current.style.width = `${spanWidth - 4}px`;
    }
  }, [spanWidth]);

  useEffect(() => {
    if (amirbios1Ref.current) {
      amirbios1Ref.current.style.width = `${tableWidth - 4}px`;
    }
  }, [tableWidth]);

  return (
    <div ref={amirbiosRef} id="amirbios" className={cls.listContainer}>
      {showBookingsModal && selectedRow && (
        <Bookings
          open={showBookingsModal}
          DATA={bookingsData}
          selectedRow={selectedRow}
          setShowBookingsModal={setShowBookingsModal}
          getExportInventoryList={getExportInventoryList}
        />
      )}
      {!tableTheadTitle ? (
        <div className="mt-16">
          <LoadingV2 />
        </div>
      ) : (
        <table
          ref={tableRef}
          id="table"
          style={{
            minWidth: (() => {
              const visibleHeaders = tableTheadTitle.filter(
                (header) => header?.show !== false
              );
              const visibleCount = visibleHeaders.length;
              if (visibleCount < 18) {
                const baseWidth = 1650;
                const reduction = (18 - visibleCount) * 150;
                return `${baseWidth - reduction}px`;
              }
              return '1650px';
            })(),
          }}
          className={cls.list}>
          <thead ref={amirbios2Ref} id="amirbios1">
            <tr ref={amirbios1Ref} id="amirbios2" className={cls.thead}>
              <th style={{ flex: 0.35 }}>#</th>
              {tableTheadTitle.map((header, index) =>
                header?.show === false ? (
                  ''
                ) : (
                  <th
                    key={index}
                    style={{ flex: header.flex }}
                    className={
                      sortableColumns[header.object] &&
                      sortConfig.key === sortableColumns[header.object]
                        ? cls.sortableHeaderSorted
                        : sortableColumns[header.object]
                        ? cls.sortableHeader
                        : ''
                    }
                    onClick={
                      sortableColumns[header.object]
                        ? () => sortBy(sortableColumns[header.object])
                        : undefined
                    }>
                    <div
                      className={`flex items-center justify-center ${
                        sortableColumns[header.object]
                          ? 'cursor-pointer'
                          : 'cursor-default'
                      }`}
                      onMouseEnter={() =>
                        sortableColumns[header.object] &&
                        setHoveredColumn(sortableColumns[header.object])
                      }
                      onMouseLeave={() => setHoveredColumn(null)}>
                      <span>{header.label}</span>
                      {sortableColumns[header.object] &&
                        (hoveredColumn === sortableColumns[header.object] ||
                          sortConfig.key ===
                            sortableColumns[header.object]) && (
                          <ReactSVG
                            src={
                              sortConfig.key === sortableColumns[header.object]
                                ? sortConfig.direction === 'ascending'
                                  ? Icons.sortedNewUp
                                  : Icons.sortedNewDown
                                : Icons.sortNew
                            }
                            className={cls.sortIcon}
                          />
                        )}
                    </div>
                  </th>
                )
              )}
              <th style={{ flex: 1.6 }}>Action</th>
            </tr>
          </thead>
          <tbody className={cls.tbody}>
            {getDisplayData().map((i, idx) =>
              i.bookings.map((booking, idd) => {
                uniqueIndex++;

                return (
                  <tr className={cls.tableRow} key={`${uniqueIndex}new`}>
                    <td style={{ flex: 0.35 }}>
                      {uniqueIndex + (currentPage - 1) * 10}
                    </td>
                    {tableTheadTitle.map((header, index) => {
                      const objectKey = header.object;

                      return header?.show === false ? (
                        ''
                      ) : (
                        <td key={`${index}new`} style={{ flex: header.flex }}>
                          {['size', 'loc'].includes(objectKey)
                            ? i[objectKey]
                              ? i[objectKey]
                              : '-'
                            : ''}
                          {['ssl'].includes(objectKey) && header.hasLink ? (
                            <a
                              target="_blank"
                              className={cls.listHasLink}
                              href={booking?.shipping_line?.links?.vessel}>
                              {i[objectKey] || '-'}
                            </a>
                          ) : ['ssl'].includes(objectKey) ? (
                            i[objectKey] ? (
                              i[objectKey]
                            ) : (
                              '-'
                            )
                          ) : (
                            ''
                          )}
                          {['name'].includes(objectKey) && header.hasLink ? (
                            <a
                              className={cls.listHasLink}
                              href={booking?.shipping_line_tracking_url}
                              onClick={(e) =>
                                handleBookingNameClick(e, booking, objectKey)
                              }>
                              {booking[objectKey] || '-'}
                            </a>
                          ) : ['name'].includes(objectKey) ? (
                            booking[objectKey] ? (
                              booking[objectKey]
                            ) : (
                              '-'
                            )
                          ) : (
                            ''
                          )}
                          {[
                            'return_appointment',
                            'return_terminals',
                            'return_dual_terminals',
                          ].includes(objectKey)
                            ? booking[objectKey]
                              ? booking[objectKey]
                              : '-'
                            : ''}
                          {objectKey === 'slug'
                            ? booking?.mto?.slug
                              ? booking?.mto?.slug
                              : '-'
                            : ''}
                          {objectKey === 'container'
                            ? booking['container']
                              ? booking['container']
                              : '-'
                            : ''}
                          {objectKey === '-' ? '-' : ''}
                          {objectKey === 'appointment_date'
                            ? booking['appointment']
                              ? fDate(booking['appointment'])
                              : '-'
                            : ''}
                          {objectKey === 'erd' ? (
                            booking['erd'] ? (
                              isValidDateTime(booking['erd']) ? (
                                <span className={cls.messageTooltip}>
                                  {formatDateTimeToUTC(booking['erd'])}
                                </span>
                              ) : (
                                <Tooltip
                                  arrow
                                  tooltipId={`erd-${booking.id}`}
                                  iconId={`erd-icon-${booking.id}`}
                                  title={booking['erd']}
                                  placement="top">
                                  <span className={cls.messageTooltip}>
                                    <ReactSVG
                                      className={cls.infoIconTooltip}
                                      src={Icons.infoIcon}
                                    />{' '}
                                    Not Reported
                                  </span>
                                </Tooltip>
                              )
                            ) : (
                              <span className={cls.messageTooltip}>- </span>
                            )
                          ) : (
                            ''
                          )}
                          {objectKey === 'gated_in'
                            ? booking['gated_in']
                              ? formatDateTimeToUTC(booking['gated_in'])
                              : '-'
                            : ''}
                          {objectKey === 'vessel_name'
                            ? booking['vessel_name']
                              ? booking['vessel_name']
                              : '-'
                            : ''}
                          {objectKey === 'vessel_voyage_in'
                            ? booking['vessel_voyage_in']
                              ? booking['vessel_voyage_in']
                              : '-'
                            : ''}
                          {objectKey === 'gated_out'
                            ? booking['gated_out']
                              ? formatDateTimeToUTC(booking['gated_out'])
                              : '-'
                            : ''}
                          {objectKey === 'load_pickup_date'
                            ? booking['load_pickup_date']
                              ? formatDateTimeToUTC(booking['load_pickup_date'])
                              : '-'
                            : ''}
                          {objectKey === 'pick_up_location'
                            ? booking?.pick_up_location?.slug
                              ? booking?.pick_up_location?.slug
                              : '-'
                            : ''}
                          {objectKey === 'vessel_departure'
                            ? booking['vessel_departure']
                              ? formatDateTimeToUTC(booking['vessel_departure'])
                              : '-'
                            : ''}
                          {['return_to_terminal'].includes(objectKey) &&
                          header.hasLink ? (
                            <a
                              target="_blank"
                              className={
                                booking?.return_to_terminal?.slug
                                  ? cls.listHasLink
                                  : ''
                              }
                              href={
                                booking?.return_to_terminal?.links
                                  ?.vessel_schedule
                              }>
                              {booking?.return_to_terminal?.slug || '-'}
                            </a>
                          ) : ['return_to_terminal'].includes(objectKey) ? (
                            booking?.return_to_terminal?.slug ? (
                              booking?.return_to_terminal?.slug
                            ) : (
                              '-'
                            )
                          ) : (
                            ''
                          )}
                          {objectKey === 'cutoff' ? (
                            booking['cutoff'] ? (
                              isValidDateTime(booking['cutoff']) ? (
                                <span className={cls.messageTooltip}>
                                  {formatDateTimeToUTC(booking['cutoff'])}
                                </span>
                              ) : (
                                <Tooltip
                                  tooltipId={`cutoff-${booking.id}`}
                                  iconId={`cutoff-icon-${booking.id}`}
                                  arrow
                                  title={booking['cutoff']}
                                  placement="top">
                                  <span className={cls.messageTooltip}>
                                    <ReactSVG
                                      className={cls.infoIconTooltip}
                                      src={Icons.infoIcon}
                                    />{' '}
                                    Not Reported
                                  </span>
                                </Tooltip>
                              )
                            ) : (
                              <span className={cls.messageTooltip}>-</span>
                            )
                          ) : (
                            ''
                          )}
                          {objectKey === 'date'
                            ? i['date']
                              ? fDate(i['date'])
                              : '-'
                            : ''}
                          {objectKey === 'up'
                            ? i['up']
                              ? fTimeDateUSFormat(i['up'])
                              : '-'
                            : ''}
                        </td>
                      );
                    })}

                    <td className={cls.tabActions} style={{ flex: 1.6 }}>
                      <IconButtonV2
                        noBorder
                        icon="pen"
                        aria-label="edit"
                        title="Edit"
                        onClick={() => Edit(i, booking.id, booking)}
                      />

                      <Tooltip
                        arrow
                        placement="top"
                        tooltipId={`booking-status-${booking.id}`}
                        iconId={`booking-status-icon-${booking.id}`}
                        title={
                          booking.status_message
                            ? booking.status_message
                            : booking.status === 1
                            ? 'Disable'
                            : 'Enable'
                        }>
                        <IconButtonV2 noBorder>
                          <CustomCheckbox
                            size="small"
                            checked={booking.status === 1 ? true : false}
                            color="black"
                            onChange={(e) =>
                              handleChangeContainerStatus(
                                e.target.checked,
                                booking.id,
                                idd
                              )
                            }
                          />
                        </IconButtonV2>
                      </Tooltip>

                      <IconButtonV2
                        noBorder
                        icon="deleteIcon"
                        aria-label="delete"
                        title="Delete"
                        onClick={() => handleDeleteClick(booking.id)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        minWidth="unset"
        noX
        show={showDeleteModal.show}
        children={
          <span className="mt-6 text-center w-full md:min-w-xl">
            Are you sure you want to delete this booking?
          </span>
        }
        submitText={'Yes, Delete'}
        icon={'deleteIcon'}
        title={'Delete Booking'}
        setShow={(value) => {
          setShowDeleteModal({
            show: value,
            itemId: showDeleteModal.itemId,
          });
        }}
        submit={handleDeleteConfirm}
        loadingbutton={false}
      />
    </div>
  );
};
